import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  useTheme,
  alpha
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import { useDomain, DOMAINS } from '../../contexts/DomainContext';
import { searchBusinessRegNo, formatBusinessRegNo, removeHyphens } from '../../api/businessRegNoApi';

const BusinessRegNoModal = ({ open, onClose, onSelect, rowData }) => {
  const theme = useTheme();
  const { domain } = useDomain();
  const isDarkMode = theme.palette.mode === 'dark';

  const [searchType, setSearchType] = useState('business'); // 'business', 'individual', or 'manual'
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  // 사업자등록번호 자동 포맷팅
  const handleBusinessRegNoChange = (value) => {
    const numbersOnly = value.replace(/[^0-9-]/g, '');
    const cleanValue = removeHyphens(numbersOnly);

    // 10자리를 초과하면 입력 제한
    if (cleanValue.length <= 10) {
      const formattedValue = formatBusinessRegNo(cleanValue);
      setSearchValue(formattedValue);
    }
  };

  // 안내문구 생성
  const getBusinessRegNoHelperText = () => {
    const cleanValue = removeHyphens(searchValue);
    if (searchValue && cleanValue.length < 10) {
      return `사업자등록번호는 10자리여야 합니다. (현재 ${cleanValue.length}자리)`;
    }
    return '';
  };

  // 도메인별 색상 설정
  const getTextColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? '#f0e6d9' : 'rgba(0, 0, 0, 0.87)';
    }
    return isDarkMode ? '#b3c5e6' : 'rgba(0, 0, 0, 0.87)';
  };

  const getPrimaryColor = () => {
    if (domain === DOMAINS.PEMS) {
      return isDarkMode ? '#f5d57a' : '#d4950a';
    }
    return theme.palette.primary.main;
  };

  // 사업자등록번호 조회 API 호출
  const handleBusinessSearch = async () => {
    if (!searchValue.trim()) {
      console.log('검색어가 비어있습니다.');
      return;
    }
    setLoading(true);

    try {
      const results = await searchBusinessRegNo(searchValue);
      // API 결과를 UI에 맞게 변환
      const formattedResults = results.map((item, index) => ({
        id: index + 1,
        businessRegNo: item.businessRegNo,
        companyName: item.companyName,
        ceoName: item.ceoName || '정보없음',
        address: item.address || '정보없음',
        businessType: item.businessType || '정보없음',
        status: item.status || '운영중',
        taxType: item.taxType || '정보없음'
      }));
      setSearchResults(formattedResults);
      setHasSearched(true);

      if (formattedResults.length === 0) {
        console.log('검색 결과가 없습니다.');
      }
    } catch (error) {
      console.error('사업자등록번호 조회 오류:', error);
      setSearchResults([]);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  };

  // 개인 선택
  const handleIndividualSelect = () => {
    onSelect({
      businessRegNo: '-',
      vendorName: '개인',
      vendorType: 'INDIVIDUAL',
      isIndividual: true
    });
    handleClose();
  };

  // 사업자 선택 (검증된 사업자등록번호만 매핑)
  const handleBusinessSelect = (item) => {
    onSelect({
      businessRegNo: item.businessRegNo,
      vendorType: 'BUSINESS',
      isIndividual: false,
      // 회사명, 대표명 등은 매핑하지 않음 (사유: 국세청 API는 진위확인만 제공)
      vendorName: '',
      ceoName: '',
      address: '',
      businessType: ''
    });
    handleClose();
  };

  // 수동 입력 선택(국세청 진위여부 확인으로 검색 안 되는 경우)
  const handleManualSelect = () => {
    onSelect({
      businessRegNo: searchValue,
      vendorName: selectedItem?.companyName || '',
      ceoName: selectedItem?.ceoName || '',
      address: selectedItem?.address || '',
      businessType: selectedItem?.businessType || '',
      vendorType: 'BUSINESS',
      isIndividual: false
    });
    handleClose();
  };

  const handleClose = () => {
    setSearchValue('');
    setSearchResults([]);
    setSelectedItem(null);
    setSearchType('business');
    setHasSearched(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: isDarkMode ? theme.palette.background.paper : '#ffffff',
          color: getTextColor()
        }
      }}
    >
      <DialogTitle sx={{
        borderBottom: `1px solid ${isDarkMode ? '#424242' : '#e0e0e0'}`,
        color: getTextColor()
      }}>
        거래처 등록 방법 선택
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* 등록 방법 선택 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: getTextColor() }}>
            등록 방법을 선택하세요
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant={searchType === 'business' ? 'contained' : 'outlined'}
              startIcon={<BusinessIcon />}
              onClick={() => setSearchType('business')}
              sx={{
                flex: 1,
                backgroundColor: searchType === 'business' ? getPrimaryColor() : 'transparent',
                borderColor: getPrimaryColor(),
                color: searchType === 'business' ? '#ffffff' : getPrimaryColor(),
                '&:hover': {
                  backgroundColor: searchType === 'business'
                    ? alpha(getPrimaryColor(), 0.8)
                    : alpha(getPrimaryColor(), 0.1)
                }
              }}
            >
              사업자 조회
            </Button>
            <Button
              variant={searchType === 'individual' ? 'contained' : 'outlined'}
              startIcon={<PersonIcon />}
              onClick={() => setSearchType('individual')}
              sx={{
                flex: 1,
                backgroundColor: searchType === 'individual' ? getPrimaryColor() : 'transparent',
                borderColor: getPrimaryColor(),
                color: searchType === 'individual' ? '#ffffff' : getPrimaryColor(),
                '&:hover': {
                  backgroundColor: searchType === 'individual'
                    ? alpha(getPrimaryColor(), 0.8)
                    : alpha(getPrimaryColor(), 0.1)
                }
              }}
            >
              개인 등록
            </Button>
          </Box>
        </Box>

        {/* 사업자 조회 */}
        {searchType === 'business' && (
          <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="사업자등록번호 (10자리)"
                value={searchValue}
                onChange={(e) => handleBusinessRegNoChange(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleBusinessSearch()}
                placeholder="사업자등록번호 10자리를 입력하세요"
                helperText={getBusinessRegNoHelperText()}
                error={!!getBusinessRegNoHelperText()}
                sx={{
                  '& .MuiInputLabel-root': { color: getTextColor() },
                  '& .MuiOutlinedInput-root': {
                    color: getTextColor(),
                    '& fieldset': { borderColor: isDarkMode ? '#424242' : '#e0e0e0' }
                  },
                  '& .MuiFormHelperText-root': {
                    color: getBusinessRegNoHelperText() ? '#f44336' : getTextColor()
                  }
                }}
              />
              <IconButton
                onClick={handleBusinessSearch}
                disabled={loading || !searchValue.trim()}
                sx={{
                  backgroundColor: getPrimaryColor(),
                  color: '#ffffff',
                  '&:hover': { backgroundColor: alpha(getPrimaryColor(), 0.8) },
                  '&:disabled': { backgroundColor: '#cccccc' }
                }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
              </IconButton>
            </Box>

            {/* 수동입력 버튼 */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setSearchType('manual')}
                sx={{
                  borderColor: getPrimaryColor(),
                  color: getPrimaryColor(),
                  '&:hover': {
                    backgroundColor: alpha(getPrimaryColor(), 0.1),
                    borderColor: getPrimaryColor()
                  }
                }}
              >
                조회가 안 되나요? 수동으로 입력하기
              </Button>
            </Box>

            {/* 검색 결과 */}
            {(searchResults.length > 0 || (searchResults.length === 0 && !loading && searchValue.trim())) && (
              <TableContainer
                component={Paper}
                sx={{
                  backgroundColor: isDarkMode ? theme.palette.background.default : '#ffffff',
                  border: `1px solid ${isDarkMode ? '#424242' : '#e0e0e0'}`,
                  mt: 2
                }}
              >
                {searchResults.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: getTextColor(), fontWeight: 600 }}>사업자등록번호</TableCell>
                        <TableCell sx={{ color: getTextColor(), fontWeight: 600 }}>사업자상태</TableCell>
                        <TableCell sx={{ color: getTextColor(), fontWeight: 600 }}>과세유형</TableCell>
                        <TableCell sx={{ color: getTextColor(), fontWeight: 600 }}>거래가능여부</TableCell>
                        <TableCell sx={{ color: getTextColor(), fontWeight: 600 }}>선택</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {searchResults.map((item) => (
                        <TableRow
                          key={item.id}
                          hover
                          sx={{
                            '&:hover': {
                              backgroundColor: isDarkMode
                                ? alpha(getPrimaryColor(), 0.1)
                                : alpha(getPrimaryColor(), 0.05)
                            }
                          }}
                        >
                          <TableCell sx={{ color: getTextColor() }}>{item.businessRegNo}</TableCell>
                          <TableCell sx={{ color: getTextColor() }}>{item.ceoName}</TableCell>
                          <TableCell sx={{ color: getTextColor() }}>{item.address}</TableCell>
                          <TableCell sx={{
                            color: item.businessType === '거래 가능' ? '#4caf50' : '#f44336',
                            fontWeight: 600
                          }}>
                            {item.businessType}
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleBusinessSelect(item)}
                              disabled={item.businessType === '거래 불가'}
                              sx={{
                                backgroundColor: item.businessType === '거래 가능' ? getPrimaryColor() : '#cccccc',
                                '&:hover': {
                                  backgroundColor: item.businessType === '거래 가능'
                                    ? alpha(getPrimaryColor(), 0.8)
                                    : '#cccccc'
                                },
                                '&:disabled': { backgroundColor: '#cccccc', color: '#666' }
                              }}
                            >
                              선택
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  // 검색 결과가 없을 때 표시
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: getTextColor(), mb: 1 }}>
                      검색 결과가 없습니다
                    </Typography>
                    <Typography variant="body2" sx={{ color: getTextColor(), opacity: 0.7 }}>
                      '{searchValue}'에 대한 검색 결과를 찾을 수 없습니다.
                    </Typography>
                    <Typography variant="body2" sx={{ color: getTextColor(), opacity: 0.7, mt: 1 }}>
                      • 사업자등록번호(10자리): 실제 국세청 데이터 조회
                    </Typography>
                  </Box>
                )}
              </TableContainer>
            )}
          </Box>
        )}

        {/* 개인 등록 */}
        {searchType === 'individual' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <PersonIcon sx={{ fontSize: 60, color: getPrimaryColor(), mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 2, color: getTextColor() }}>
              개인 거래처로 등록
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: getTextColor() }}>
              사업자등록번호가 없는 개인 거래처로 등록됩니다.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleIndividualSelect}
              sx={{
                backgroundColor: getPrimaryColor(),
                '&:hover': { backgroundColor: alpha(getPrimaryColor(), 0.8) }
              }}
            >
              개인으로 등록
            </Button>
          </Box>
        )}

        {/* 수동 입력 */}
        {searchType === 'manual' && (
          <Box>
            <Typography variant="h6" sx={{ mb: 3, color: getTextColor() }}>
              사업자 정보 직접 입력
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                size="small"
                label="사업자등록번호 (10자리)"
                value={searchValue}
                onChange={(e) => handleBusinessRegNoChange(e.target.value)}
                placeholder="1234567890"
                helperText={getBusinessRegNoHelperText()}
                error={!!getBusinessRegNoHelperText()}
                sx={{
                  '& .MuiInputLabel-root': { color: getTextColor() },
                  '& .MuiOutlinedInput-root': {
                    color: getTextColor(),
                    '& fieldset': { borderColor: isDarkMode ? '#424242' : '#e0e0e0' }
                  },
                  '& .MuiFormHelperText-root': {
                    color: getBusinessRegNoHelperText() ? '#f44336' : getTextColor()
                  }
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="회사명"
                value={selectedItem?.companyName || ''}
                onChange={(e) => setSelectedItem(prev => ({ ...prev, companyName: e.target.value }))}
                placeholder="회사명을 입력하세요"
                sx={{
                  '& .MuiInputLabel-root': { color: getTextColor() },
                  '& .MuiOutlinedInput-root': {
                    color: getTextColor(),
                    '& fieldset': { borderColor: isDarkMode ? '#424242' : '#e0e0e0' }
                  }
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="대표자명"
                value={selectedItem?.ceoName || ''}
                onChange={(e) => setSelectedItem(prev => ({ ...prev, ceoName: e.target.value }))}
                placeholder="대표자명을 입력하세요"
                sx={{
                  '& .MuiInputLabel-root': { color: getTextColor() },
                  '& .MuiOutlinedInput-root': {
                    color: getTextColor(),
                    '& fieldset': { borderColor: isDarkMode ? '#424242' : '#e0e0e0' }
                  }
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="주소"
                value={selectedItem?.address || ''}
                onChange={(e) => setSelectedItem(prev => ({ ...prev, address: e.target.value }))}
                placeholder="주소를 입력하세요"
                sx={{
                  '& .MuiInputLabel-root': { color: getTextColor() },
                  '& .MuiOutlinedInput-root': {
                    color: getTextColor(),
                    '& fieldset': { borderColor: isDarkMode ? '#424242' : '#e0e0e0' }
                  }
                }}
              />
              <TextField
                fullWidth
                size="small"
                label="업종"
                value={selectedItem?.businessType || ''}
                onChange={(e) => setSelectedItem(prev => ({ ...prev, businessType: e.target.value }))}
                placeholder="업종을 입력하세요"
                sx={{
                  '& .MuiInputLabel-root': { color: getTextColor() },
                  '& .MuiOutlinedInput-root': {
                    color: getTextColor(),
                    '& fieldset': { borderColor: isDarkMode ? '#424242' : '#e0e0e0' }
                  }
                }}
              />
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setSearchType('business')}
                  sx={{
                    flex: 1,
                    borderColor: getPrimaryColor(),
                    color: getPrimaryColor(),
                    '&:hover': {
                      backgroundColor: alpha(getPrimaryColor(), 0.1),
                      borderColor: getPrimaryColor()
                    }
                  }}
                >
                  돌아가기
                </Button>
                <Button
                  variant="contained"
                  onClick={handleManualSelect}
                  disabled={!searchValue.trim() || !selectedItem?.companyName?.trim()}
                  sx={{
                    flex: 1,
                    backgroundColor: getPrimaryColor(),
                    '&:hover': { backgroundColor: alpha(getPrimaryColor(), 0.8) },
                    '&:disabled': { backgroundColor: '#cccccc' }
                  }}
                >
                  등록
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{
        borderTop: `1px solid ${isDarkMode ? '#424242' : '#e0e0e0'}`,
        p: 2
      }}>
        <Button
          onClick={handleClose}
          sx={{ color: getTextColor() }}
        >
          취소
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BusinessRegNoModal;